/**
 * Change Requests List Component
 * Displays all change requests for a BOQ with actions
 */

import { Check, Clock, MessageSquare, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SkeletonRows } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import { Textarea } from "@/components/ui/textarea";
import {
	approveBOQChangeRequest,
	type BOQ,
	type BOQChangeRequest,
	getBOQChangeRequests,
	getStatusColor,
	rejectBOQChangeRequest,
} from "../../services/boq/boq-api.service";

interface ChangeRequestsListProps {
	boq: BOQ;
	/** Bumped by the page when a request is added, to reload the list. */
	refreshTrigger?: number;
	/** Whether the viewer is the project's designer or hired contractor. */
	canResolve: boolean;
	/** Called after a request is answered, so the page reloads the BOQ's status. */
	onResolved?: () => void;
}

/** A request's status as this list names it: the backend's approved/rejected. */
const RESOLUTION_LABEL: Record<BOQChangeRequest["status"], string> = {
	open: "Open",
	approved: "Accepted",
	rejected: "Declined",
};

/** Upper bound the backend validates `resolutionNote` against. */
const NOTE_MAX = 5000;

export function ChangeRequestsList({
	boq,
	refreshTrigger,
	canResolve,
	onResolved,
}: ChangeRequestsListProps) {
	const [changeRequests, setChangeRequests] = useState<BOQChangeRequest[]>([]);
	// The skeleton is for the first load only; a reload keeps the list showing.
	const [loading, setLoading] = useState(true);
	// Requests with an answer in flight, so answering one does not re-enable
	// another's buttons when it finishes.
	const [answering, setAnswering] = useState<ReadonlySet<string>>(new Set());
	const [notes, setNotes] = useState<Record<string, string>>({});
	// Only the latest load may write the list; an earlier one finishing late
	// would put back requests as they were before an answer.
	const latestLoad = useRef(0);

	const fetchChangeRequests = useCallback(async () => {
		const load = ++latestLoad.current;
		try {
			const result = await getBOQChangeRequests(boq.id);
			if (load !== latestLoad.current) return;
			if (result.error) {
				console.error("Failed to load change requests:", result.error);
				setChangeRequests([]);
			} else {
				setChangeRequests(result.data);
			}
		} catch (error) {
			console.error("Failed to load change requests:", error);
		} finally {
			if (load === latestLoad.current) setLoading(false);
		}
	}, [boq.id]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: refreshTrigger is how the page asks for a reload.
	useEffect(() => {
		void fetchChangeRequests();
	}, [fetchChangeRequests, refreshTrigger]);

	const resolve = async (
		changeRequestId: string,
		outcome: "accept" | "decline",
	) => {
		setAnswering((prev) => new Set(prev).add(changeRequestId));
		const note = notes[changeRequestId]?.trim() || undefined;
		try {
			const result =
				outcome === "accept"
					? await approveBOQChangeRequest(changeRequestId, note)
					: await rejectBOQChangeRequest(changeRequestId, note);
			if (result.success) {
				toast.success(
					outcome === "accept"
						? "Change request accepted"
						: "Change request declined",
				);
				await fetchChangeRequests();
				onResolved?.();
			} else {
				toast.error(result.error || "Failed to answer the change request");
			}
		} catch (_error) {
			toast.error("An unexpected error occurred");
		} finally {
			setAnswering((prev) => {
				const next = new Set(prev);
				next.delete(changeRequestId);
				return next;
			});
		}
	};

	if (loading) {
		return (
			<SkeletonRows
				label="Loading change requests..."
				rows={2}
				className="py-4"
			/>
		);
	}

	if (changeRequests.length === 0) {
		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					padding: "36px 20px",
					textAlign: "center",
				}}
			>
				<MessageSquare
					size={32}
					style={{
						color: "var(--color-dashboard-faint-text)",
						marginBottom: 12,
					}}
				/>
				<h3
					style={{
						fontFamily: "var(--font-display)",
						fontStyle: "italic",
						fontSize: 16,
						fontWeight: 500,
						color: "var(--color-dashboard-muted-dark)",
						margin: "0 0 6px 0",
					}}
				>
					No change requests
				</h3>
				<p
					style={{
						fontFamily: "var(--font-body)",
						fontSize: 13,
						color: "var(--color-dashboard-placeholder)",
						margin: 0,
						maxWidth: 320,
						lineHeight: 1.4,
					}}
				>
					No change requests have been submitted for this BOQ yet.
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{changeRequests.map((request) => (
				<div
					key={request.id}
					className="rounded-lg border bg-card p-4 shadow-sm"
				>
					<div className="flex flex-wrap items-start justify-between gap-2 mb-3">
						<div className="flex flex-wrap items-center gap-2 min-w-0">
							<StatusBadge
								status={RESOLUTION_LABEL[request.status]}
								className={getStatusColor(request.status)}
							/>
							<span className="text-sm text-muted-foreground">
								{new Date(request.createdAt).toLocaleDateString()} at{" "}
								{new Date(request.createdAt).toLocaleTimeString()}
							</span>
						</div>
						{request.status === "open" && canResolve && (
							<div className="flex gap-2">
								<Button
									variant="outline"
									size="sm"
									onClick={() => resolve(request.id, "accept")}
									disabled={answering.has(request.id)}
									className="text-dashboard-pill-done hover:text-dashboard-success hover:bg-dashboard-success-bg"
								>
									<Check className="h-4 w-4 mr-1" />
									Accept
								</Button>
								<Button
									variant="outline"
									size="sm"
									onClick={() => resolve(request.id, "decline")}
									disabled={answering.has(request.id)}
									className="text-dashboard-pill-risk hover:text-dashboard-danger hover:bg-dashboard-danger-bg"
								>
									<X className="h-4 w-4 mr-1" />
									Decline
								</Button>
							</div>
						)}
					</div>

					<div className="space-y-2">
						<div className="flex items-start gap-2">
							<span className="text-sm font-medium text-muted-foreground min-w-20">
								From:
							</span>
							<span className="text-sm">
								{request.requestedByUser?.name || "Unknown User"}
							</span>
						</div>

						<div className="flex items-start gap-2">
							<span className="text-sm font-medium text-muted-foreground min-w-20">
								Message:
							</span>
							<p className="text-sm whitespace-pre-wrap flex-1">
								{request.message}
							</p>
						</div>

						{request.status === "open" && canResolve && (
							<div className="mt-3 pt-3 border-t space-y-2">
								<label
									htmlFor={`cr-note-${request.id}`}
									className="text-sm font-medium text-muted-foreground"
								>
									Response to the homeowner (optional)
								</label>
								<Textarea
									id={`cr-note-${request.id}`}
									rows={2}
									maxLength={NOTE_MAX}
									value={notes[request.id] ?? ""}
									onChange={(event) =>
										setNotes((prev) => ({
											...prev,
											[request.id]: event.target.value,
										}))
									}
								/>
								<p className="text-xs text-muted-foreground">
									Accept or Decline records your answer and tells the homeowner;
									the BOQ stays editable either way. Once every request is
									answered and the accepted changes are made, press Send to
									Homeowner.
								</p>
							</div>
						)}

						{request.status !== "open" && request.resolutionNote && (
							<div className="flex items-start gap-2 mt-3 pt-3 border-t">
								<span className="text-sm font-medium text-muted-foreground min-w-20">
									Response:
								</span>
								<p className="text-sm whitespace-pre-wrap flex-1">
									{request.resolutionNote}
								</p>
							</div>
						)}

						{request.status !== "open" && (
							<div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
								<Clock className="h-3 w-3" />
								<span>
									Resolved by {request.resolvedByUser?.name || "Unknown"} on{" "}
									{request.resolvedAt &&
										new Date(request.resolvedAt).toLocaleDateString()}
								</span>
							</div>
						)}
					</div>
				</div>
			))}
		</div>
	);
}
